import { getGuestGenderByTitle, isGuestAlreadyInArray } from '@utils/helpers';
import { useFormContext } from 'react-hook-form';
import { Guest } from 'shared-types/Basket';
import { MappedGuest } from '../../types';
import { usePackageConfiguration } from '../../contexts';

type GuestFormProps = {
  availableGuests: MappedGuest[];
  basketGuests: Guest[];
};

type GuestWithDetails = {
  guestDetails: {
    firstName?: string;
    lastName?: string;
    title?: string;
    gender?: string;
  };
  roomNumber: number;
  experiences: any[];
};

function findEmptyGuest(guests: GuestWithDetails[]): number | null {
  const index = guests.findIndex(
    (guest) => !guest?.guestDetails?.firstName && !guest?.guestDetails?.lastName
  );
  return index >= 0 ? index : null;
}

function findMatchingGuest(guests: GuestWithDetails[], existingGuest: MappedGuest): number | null {
  const index = guests.findIndex(
    (guest) =>
      guest?.guestDetails?.firstName === existingGuest.firstName &&
      guest?.guestDetails?.lastName === existingGuest.lastName
  );
  return index >= 0 ? index : null;
}

export function useAddExistingGuest({ availableGuests, basketGuests }: GuestFormProps) {
  const form = useFormContext();
  const { setValue, watch } = form;
  const { journeyParams } = usePackageConfiguration();
  const guests = watch('guests') || [];
  const selectedGuests = guests.map((g) => g?.guestDetails).filter(Boolean);

  const getRoomNumberForGuestIndex = (guestIndex: number): number => {
    if (!journeyParams?.rooms) return 0;

    let cumulativeGuests = 0;
    for (let roomIndex = 0; roomIndex < journeyParams.rooms.length; roomIndex++) {
      const room = journeyParams.rooms[roomIndex];
      cumulativeGuests += room.adults;
      if (guestIndex < cumulativeGuests) {
        return roomIndex;
      }
    }
    return 0;
  };

  const handleGuestPillClick = ({ guest }) => {
    const { title, firstName, lastName, gender } = guest;
    const mappedGuest = {
      title,
      firstName,
      lastName,
      gender: gender ?? getGuestGenderByTitle(guest),
    };

    if (!isGuestAlreadyInArray(selectedGuests, mappedGuest)) {
      const emptyGuestIndex = findEmptyGuest(guests);

      if (emptyGuestIndex !== null) {
        const roomNumber = getRoomNumberForGuestIndex(emptyGuestIndex);
        setValue(`guests.${emptyGuestIndex}.guestDetails`, mappedGuest, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue(`guests.${emptyGuestIndex}.roomNumber`, roomNumber);
      }
    } else {
      const matchingGuestIndex = findMatchingGuest(guests, guest);

      if (matchingGuestIndex !== null) {
        setValue(
          `guests.${matchingGuestIndex}.guestDetails`,
          { title: '', firstName: '', lastName: '', gender: '' },
          { shouldValidate: true, shouldDirty: true }
        );
      }
    }
  };

  const existingGuests = [...availableGuests];
  basketGuests.forEach((guest) => {
    const existingGuest = availableGuests.find(
      (g) => g.firstName === guest.firstName && g.lastName === guest.lastName
    );
    if (!existingGuest) {
      existingGuests.push({
        title: guest.title,
        firstName: guest.firstName,
        lastName: guest.lastName,
        gender: guest.gender,
      });
    }
  });
  return { handleGuestPillClick, selectedGuests, existingGuests };
}
